import { IkeToken } from '@ikegps/core-api';
import { computed, makeObservable } from 'mobx';
import { T, Translate } from '../../i18n';
import { m } from '../../mithril';
import type { MithrilVNode } from '../../mithril';
import { getLongRunningTaskManager } from '../../modules/long.running.task.manager';
import { DepartmentSelector } from '../../modules/department.selector/department.selector';
import { OmniSearch } from '../../modules/omni.search/omni.search';
import { ReleaseNotesPopup } from '../../modules/release.notes.popup/release.notes.popup';
import { ChangeSettingsComponent } from '../../modules/user/change.settings';
import { IkePageName } from '../../page/page';
import { PageLayoutState } from '../../page/page.layout';
import { Services } from '../../services';
import { userDisplayName } from '../../util/userDisplayName';
import { ImageButton } from '../button/image.button';
import { Icon } from '../icon';
import { MaterialIcon } from '../icon/icons';
import { MenuComponent } from '../menu/menu';
import { type MenuItem, MenuItemComponent } from '../menu/menu.item';
import { IMithrilComponent } from '../mithril.component';
import { ModalService } from '../modal/modal.service';
import { redDot } from '../red.dot/red.dot';
import { assetPath } from '../theme';
import { CSS, MDL } from './banner.style';

export const ROUTES = {
	FORM_LIST: () => Services.Route.go(IkePageName.formList),
	ORG_SEARCH: () => Services.Route.go(IkePageName.orgSearch),
	ACCOUNTS: () => Services.Route.go(IkePageName.accountList),
	LOGOUT: () => Services.Route.go(IkePageName.logout),
};

export class BannerComponent extends IMithrilComponent {
	name = 'Banner';

	departmentSelector: DepartmentSelector;
	changeSettings: ChangeSettingsComponent;
	omniSearch: OmniSearch;
	userMenu: MenuComponent;
	helpmenu: MenuComponent;

	constructor() {
		super();
		makeObservable(this);

		this.departmentSelector = new DepartmentSelector();
		this.changeSettings = new ChangeSettingsComponent();
		this.omniSearch = new OmniSearch();
		this.userMenu = new MenuComponent({
			items: () => this.viewUserMenuItems,
		});
		this.helpmenu = new MenuComponent({
			items: () => this.viewHelpMenuItems,
		});
	}

	@computed get nodes(): MithrilVNode[] {
		if (!Services.User.hasUser) {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			Services.User.logout();
			return null;
		}

		document.title = Translate(Services.Token.has(IkeToken.FEATURE_IKE_OFFICE_PRO) ? T.IKE_OFFICE_PRO : T.IKE_OFFICE);
		const asset = Services.Token.has(IkeToken.FEATURE_IKE_OFFICE_PRO)
			? assetPath('/images/Logo-IKE-Office-Pro.svg')
			: assetPath('/images/Logo-IKE-Office.svg');
		const officeImg = m('img', { src: asset, alt: 'IKE Office Logo', style: 'height: 35px; margin-right: 5px;' });

		const isMap = Services.Route.active.config.name === IkePageName.map;
		return [
			m('header', { className: CSS.header.container }, [
				m('div', { className: CSS.header.row }, [
					this.viewDrawerButton,
					m('div', { className: CSS.header.section }, [
						m('span', { className: MDL.title }, officeImg),
						this.departmentSelector.view(),
					]),
					isMap ? this.omniSearch.view() : null,
					m('div', { className: `${CSS.header.section} ${CSS.header.sectionRight}` }, [
						Services.Token.has(IkeToken.FEATURE_NEW_EXPORT_UI) ? getLongRunningTaskManager().view() : null,
						this.helpmenu.view(),
						this.viewHelpIcon,
						this.viewUserMenu,
						this.userMenu.view(),
					]),
				]),
			]),
		];
	}

	get viewFormIcon() {
		return ImageButton.view(MaterialIcon.CONTENT_PASTE, { onclick: ROUTES.FORM_LIST, title: T.USER_MENU_FORM });
	}

	get viewSearchIcon() {
		if (!Services.Token.has(IkeToken.ORG_SEARCH_PAGE) && !Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE)) {
			return null;
		}
		return ImageButton.view(MaterialIcon.ORG_SEARCH, { onclick: ROUTES.ORG_SEARCH, title: T.ORG_SEARCH });
	}

	get viewDrawerButton() {
		return ImageButton.view(MaterialIcon.MENU, {
			onclick: () => PageLayoutState.nav.setVisible(true),
			title: 'menu',
		});
	}

	get viewUserMenu() {
		const currentUser = Services.User.current;
		if (currentUser == null) {
			return null;
		}

		const displayName = userDisplayName(currentUser);
		return m(
			'a',
			{
				className: CSS.header.userIconWrapper,
				title: Translate(T.USER_MENU_SETTINGS),
				onclick: this.userMenu.show,
			},
			[
				m('span', { className: CSS.header.userEmail }, displayName),
				Icon.view(MaterialIcon.PERSON, { className: CSS.header.userIcon }),
			]
		);
	}

	get viewHelpIcon(): MithrilVNode {
		const showDot = Services.ReleaseNotes.hasUnreadReleaseNotes;
		return m('div', { className: CSS.header.helpIconWrapper }, [
			ImageButton.view(MaterialIcon.HELP_OUTLINE, {
				onclick: (e) => this.helpmenu.show(e),
				title: T.HELP_MENU,
			}),
			showDot ? redDot({ overlay: true }) : null,
		]);
	}

	get viewHelpMenuItems(): MenuItem[] {
		const showDot = Services.ReleaseNotes.hasUnreadReleaseNotes;
		const latestVersion = Services.ReleaseNotes.latestVersion;
		const releaseNotesLabel: MithrilVNode = m('span', { className: CSS.menuItemWithDot }, [
			m('span', Translate(T.RELEASE_NOTES)),
			showDot ? redDot() : null,
		]);

		const items: MenuItem[] = [
			{
				view: MenuItemComponent.view(Translate(T.SUBMIT_SUPPORT_REQUEST), MaterialIcon.CONTACT_SUPPORT),
				action: () => window.open('https://support.ikegps.com/hc/en-us/requests/new?ticket_form_id=5786678993677'),
				visible: true,
			},
			{
				view: MenuItemComponent.view(Translate(T.SUPPORT_PORTAL), MaterialIcon.SUPPORT),
				action: () => window.open('https://support.ikegps.com'),
				visible: true,
			},
			{
				view: MenuItemComponent.view(releaseNotesLabel, MaterialIcon.DESCRIPTION),
				action: () => {
					void ReleaseNotesPopup.open();
				},
				visible: true,
				divider: latestVersion != null,
			},
		];
		if (latestVersion != null) {
			items.push({
				view: m('div', { className: CSS.versionLabel }, `${Translate(T.VERSION_LABEL)} v${latestVersion}`),
				component: true,
				disabled: true,
			});
		}
		return items;
	}

	get viewUserMenuItems(): MenuItem[] {
		return [
			{
				view: this.viewUserCard,
				component: true,
				disabled: true,
			},
			{
				view: MenuItemComponent.view(Translate(T.USER_MENU_ACCOUNTS)),
				action: ROUTES.ACCOUNTS,
				visible: Services.Token.has(IkeToken.COMPANY_ACCOUNTS),
				testId: 'user-menu-accounts',
			},
			{
				view: MenuItemComponent.view(Translate(T.USER_MENU_SETTINGS)),
				action: () => this.openChangeSettings(),
				testId: 'user-menu-settings',
			},
			{
				view: MenuItemComponent.view(Translate(T.USER_MENU_CHANGE_PASSWORD)),
				action: () => this.openChangePassword(),
				divider: true,
				testId: 'user-menu-change-password',
			},
			{
				view: MenuItemComponent.view(Translate(T.USER_MENU_LOGOUT)),
				action: ROUTES.LOGOUT,
				testId: 'user-menu-logout',
			},
		].filter(Boolean);
	}

	get viewUserCard(): MithrilVNode {
		const currentUser = Services.User.current;
		if (currentUser == null) {
			return null;
		}

		const displayName = userDisplayName(currentUser);

		return m('div', { className: CSS.user.card, onclick: (e) => e.stopPropagation() }, [
			m('div', { className: CSS.user.name }, displayName),
			displayName !== currentUser.username ? m('div', { className: CSS.user.email }, currentUser.username) : null,
		]);
	}

	openChangeSettings() {
		this.changeSettings.open();
	}

	async openChangePassword() {
		const result = await ModalService.open({
			title: Translate(T.PASSWORD_CHANGE_PASSWORD),
			content: m('div', Translate(T.PASSWORD_CHANGE_PASSWORD_DETAIL)),
			hasCancel: true,
			okName: T.SEND,
		});

		if (result.ok) {
			await Services.SSO.resetPassword();

			const okResult = await ModalService.open({
				title: Translate(T.PASSWORD_CHANGE_PASSWORD),
				content: Translate(T.PASSWORD_RESET_EMAIL_SENT),
				okName: T.LOGOUT,
			});

			if (okResult.ok) {
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				Services.SSO.logout(true);
			}
		}
	}
}
